import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/router'

import { graphql as graphqlApiKey } from 'ms-client/graphql/api-key'
import { graphql as graphqlUserPool } from 'ms-client/graphql/user-pool'
import { mapsSortOptions } from 'ui-components'
import { isAuthenticated } from 'user-auth'
import { mapsPerPage } from '~workspace/constants/maps'
import {
  SearchMapsQuery,
  SearchMapsQueryVariables,
  Sort_Order
} from '~workspace/types/__generated/gql/graphql'

export const searchMapsQuery = /* GraphQL */ `
  query SearchMaps(
    $workspaceId: ID!
    $limit: Int!
    $offset: Int
    $tagIds: [String!]
    $query: String
    $sortBy: SearchMapsSortByInput
  ) {
    searchMaps(
      workspaceId: $workspaceId
      from: $offset
      size: $limit
      tagIds: $tagIds
      query: $query
      sortBy: $sortBy
      includePrivate: true
    ) {
      items {
        id
        createdAt
        updatedAt
        name
        description
        dataId
        views
        isPublic
        properties {
          name
          type
          visualisation
          isAggregate
          isPercentage
          isDefaultVisualisation
          isTitle
        }
        creator {
          id
          firstName
          lastName
          avatar
        }
        mapTagCollection {
          total
          items {
            tag {
              id
              name
              description
              type
              count
            }
          }
        }
      }
      total
    }
  }
`

export type SearchMapsVariables = SearchMapsQueryVariables
export type SearchMapsResults = SearchMapsQuery['searchMaps']
export const searchMaps = async (variables: SearchMapsVariables) => {
  const bool = await isAuthenticated()
  const graphql = bool ? graphqlUserPool : graphqlApiKey
  const data = await graphql<SearchMapsQuery, SearchMapsVariables>({
    query: searchMapsQuery,
    variables
  })

  return data?.searchMaps
}

export const getSearchMapsQueryKey = (variables: SearchMapsVariables) => [
  'searchMaps',
  variables
]

export const useSearchMaps = (
  variables: SearchMapsVariables
): UseQueryResult<SearchMapsResults> => {
  const query = useQuery({
    queryKey: getSearchMapsQueryKey(variables),
    queryFn: () => searchMaps(variables)
  })

  return query
}

export const useSearchMapsInCurrentWorkspace = () => {
  const {
    query: { workspaceId, page, query, tags, sortBy }
  } = useRouter()

  const [field, order] = (sortBy || mapsSortOptions[1]!.value)
    .toString()
    .split('|') as [string, Sort_Order]

  return useSearchMaps({
    workspaceId: String(workspaceId),
    limit: mapsPerPage,
    offset: Number(page) ? (parseInt(String(page)) - 1) * mapsPerPage : 0,
    query: query?.toString() || '',
    tagIds: tags?.toString().split(',') || [],
    sortBy: { field, order }
  })
}
